import {startOfDay} from 'date-fns';
import {
  Actions,
  Button,
  Dialog,
  Form,
  FormField,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {HStack, Show, Space, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {z} from 'zod';

import {
  useCorrectVehiclePurchaseDateMutation,
  useCorrectVehicleSaleDateMutation,
  VehicleSalesAction,
} from '@dms/api/vehicle';
import {i18n} from '@dms/i18n';
import {
  EMPTY_PLACEHOLDER,
  handleApiError,
  usePermissions,
  useSaleVehicleActions,
} from '@dms/shared';

import {
  getApiDateString,
  localDateToUtcDate,
  Nullish,
  suffixTestId,
  TestIdProps,
  useBoolean,
} from 'shared/web';

const SELLING_DATE_ACTION: VehicleSalesAction = 'CORRECT_SALE_DATE';
const BUYING_DATE_ACTION: VehicleSalesAction = 'CORRECT_PURCHASE_DATE';

interface VehicleSaleDatesInsightItemProps extends TestIdProps {
  vehicleId: string;
  sellingDate: Date | Nullish;
  buyingDate: Date | Nullish;
}

export function VehicleSaleDatesInsightItem(props: VehicleSaleDatesInsightItemProps) {
  const {isSaleActionEnabled} = useSaleVehicleActions(props.vehicleId);
  const formatDateTime = useDateTimeFormatter();

  const [correctSaleDate, {isLoading: isCorrectSaleDateLoading}] =
    useCorrectVehicleSaleDateMutation();
  const [correctPurchaseDate, {isLoading: isCorrectPurchaseDateLoading}] =
    useCorrectVehiclePurchaseDateMutation();

  const [canCorrectSaleDate, canCorrectPurchaseDate] = usePermissions({
    permissionKeys: ['vehicleCorrectSaleDate', 'vehicleCorrectPurchaseDate'],
  });

  const canEditSellingDate = canCorrectSaleDate && isSaleActionEnabled(SELLING_DATE_ACTION);
  const canEditBuyingDate = canCorrectPurchaseDate && isSaleActionEnabled(BUYING_DATE_ACTION);

  const handleEditSellingDate = (date: Date) =>
    correctSaleDate({
      vehicleId: props.vehicleId,
      correctVehicleSaleDateRequestBody: {
        saleDate: getApiDateString(localDateToUtcDate(date)),
      },
    })
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'));
        return true;
      })
      .catch((error: Error) => {
        handleApiError(error);
        return false;
      });

  const handleEditBuyingDate = (date: Date) =>
    correctPurchaseDate({
      vehicleId: props.vehicleId,
      correctVehiclePurchaseDateRequestBody: {
        purchaseDate: getApiDateString(localDateToUtcDate(date)),
      },
    })
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'));
        return true;
      })
      .catch((error: Error) => {
        handleApiError(error);
        return false;
      });

  const formattedSellingDate = props.sellingDate
    ? formatDateTime('dateShort', props.sellingDate)
    : EMPTY_PLACEHOLDER;
  const formattedBuyingDate = props.buyingDate
    ? formatDateTime('dateShort', props.buyingDate)
    : EMPTY_PLACEHOLDER;

  return (
    <VStack spacing={8}>
      <DateRow
        label={i18n.t('entity.vehicle.labels.overview.sellingDate')}
        editDialogLabel={i18n.t('entity.vehicle.labels.overview.editModal.sellingDate')}
        formattedDate={formattedSellingDate}
        date={props.sellingDate}
        onEdit={canEditSellingDate ? handleEditSellingDate : undefined}
        isMutationLoading={isCorrectSaleDateLoading}
        data-testid={suffixTestId('sellingDate', props)}
      />
      <DateRow
        label={i18n.t('entity.vehicle.labels.overview.buyingDate')}
        editDialogLabel={i18n.t('entity.vehicle.labels.overview.editModal.buyingDate')}
        formattedDate={formattedBuyingDate}
        date={props.buyingDate}
        onEdit={canEditBuyingDate ? handleEditBuyingDate : undefined}
        isMutationLoading={isCorrectPurchaseDateLoading}
        data-testid={suffixTestId('buyingDate', props)}
      />
    </VStack>
  );
}

interface DateRowProps extends TestIdProps {
  label: string;
  editDialogLabel: string;
  formattedDate: string;
  date: Date | Nullish;
  onEdit?: (date: Date) => Promise<boolean>;
  isMutationLoading?: boolean;
}

function DateRow(props: DateRowProps) {
  const [isEditDialogOpen, openEditDialog, closeEditDialog] = useBoolean(false);

  const defaultDate = props.date ?? startOfDay(new Date());

  const onSubmit: FormSubmitHandler<FormValues> = async (values) => {
    const isSuccess = await props.onEdit?.(values.editDate);
    if (isSuccess) {
      closeEditDialog();
    }
  };

  return (
    <>
      <VStack spacing={2}>
        <Text color="secondary" size="small">
          {props.label}
        </Text>
        <HStack align="center" spacing={4}>
          <Text alternative>{props.formattedDate}</Text>
          <Show when={props.onEdit}>
            <Actions
              actions={[
                {
                  variant: 'link',
                  type: 'button',
                  leftIcon: 'image/edit',
                  title: i18n.t('general.actions.edit'),
                  onClick: openEditDialog,
                },
              ]}
              data-testid={suffixTestId('actionsWrapper', props)}
            />
          </Show>
        </HStack>
      </VStack>

      <Dialog
        isOpen={isEditDialogOpen}
        onClose={closeEditDialog}
        size="small"
        title={i18n.t('entity.vehicle.labels.overview.editModal.title')}
        data-testid={suffixTestId('dialog', props)}
      >
        <Form<FormValues>
          onSubmit={onSubmit}
          experimentalZodSchema={schema}
          defaultValues={{editDate: defaultDate}}
        >
          {(control) => (
            <>
              <FormField
                name="editDate"
                control={control}
                type="date"
                label={props.editDialogLabel}
                data-testid={suffixTestId('dialog-dateField', props)}
              />
              <Space vertical={6} />
              <HStack justify="flex-end" spacing={2}>
                <Button
                  variant="secondary"
                  onClick={closeEditDialog}
                  title={i18n.t('general.actions.discard')}
                  isDisabled={props.isMutationLoading}
                  data-testid={suffixTestId('dialog-cancel', props)}
                />
                <Button
                  variant="primary"
                  type="submit"
                  isLoading={props.isMutationLoading}
                  title={i18n.t('general.actions.save')}
                  data-testid={suffixTestId('dialog-confirm', props)}
                />
              </HStack>
            </>
          )}
        </Form>
      </Dialog>
    </>
  );
}

const schema = z.object({
  editDate: z.date().refine((date) => date <= new Date(), {
    message: i18n.t('entity.vehicle.labels.overview.editModal.dateError'),
  }),
});

type FormValues = z.infer<typeof schema>;
